import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Shield, FileText, Zap, Mail, Eye, Clock, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

const ICON_MAP = {
  shield: Shield, 'file-text': FileText, zap: Zap, mail: Mail,
  eye: Eye, clock: Clock, refresh: RefreshCw,
};

// Direct Unsplash image IDs to avoid URL encoding issues
const CATEGORY_IMAGES = {
  'Handbags': 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400&q=80',
  'Watches': 'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=400&q=80',
  'Shoes': 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=400&q=80',
  'Clothing': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80',
  'Accessories': 'https://images.unsplash.com/photo-1509281373149-e957c6296406?w=400&q=80',
  'Jewelry': 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=400&q=80',
};

export default function FeatureGridSection({ section }) {
  const { title, subtitle, data = {} } = section;
  const items = data.items || [];
  if (items.length === 0) return null;
  const isCategoryGrid = items.some(item => item.image || CATEGORY_IMAGES[item.title]);

  return (
    <section className="py-16 sm:py-20" data-testid="section-feature-grid">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
        {(title || subtitle) && (
          <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.35 }}>
            {subtitle && <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{subtitle}</p>}
            {title && <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-[-0.015em] text-ink mb-10">{title}</h2>}
          </motion.div>
        )}
        <div className={`grid gap-4 ${isCategoryGrid ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6' : items.length <= 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
          {items.map((item, i) => {
            const Icon = ICON_MAP[item.icon];
            const catImage = item.image || CATEGORY_IMAGES[item.title];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.35, delay: i * 0.06 }}>
                {item.href ? (
                  <Link to={item.href} className="no-underline block h-full">
                    <Card className="bg-white border-stone/50 hover:shadow-[var(--shadow-pop)] transition-shadow duration-200 h-full overflow-hidden group">
                      {catImage && (
                        <div className="h-28 overflow-hidden bg-stone/10">
                          <img src={catImage} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                        </div>
                      )}
                      <CardContent className={catImage ? 'p-3' : 'p-5'}>
                        {Icon && !catImage && <div className="h-9 w-9 rounded-full flex items-center justify-center mb-2" style={{ background: 'hsl(var(--bg-paper-2))' }}><Icon className="h-4 w-4 text-ink" /></div>}
                        {item.badge && <Badge variant="outline" className="text-[10px] mb-2">{item.badge}</Badge>}
                        <h3 className={`font-semibold text-ink ${catImage ? 'text-sm' : 'text-base'} mb-1`}>{item.title}</h3>
                        {item.description && <p className={`text-muted-foreground leading-relaxed ${catImage ? 'text-[11px]' : 'text-sm'} mb-2`}>{item.description}</p>}
                        {item.details && <ul className="space-y-1 mb-3">{item.details.map((d,j)=><li key={j} className="text-xs text-muted-foreground">- {d}</li>)}</ul>}
                        {!catImage && <span className="inline-flex items-center gap-1 text-xs text-ink font-medium">Learn More <ArrowRight className="h-3 w-3" /></span>}
                      </CardContent>
                    </Card>
                  </Link>
                ) : (
                  <Card className="bg-white border-stone/50 h-full">
                    <CardContent className="p-5">
                      {Icon && <div className="h-9 w-9 rounded-full flex items-center justify-center mb-2" style={{ background: 'hsl(var(--bg-paper-2))' }}><Icon className="h-4 w-4 text-ink" /></div>}
                      {item.badge && <Badge variant="outline" className="text-[10px] mb-2">{item.badge}</Badge>}
                      <h3 className="text-base font-semibold text-ink mb-2">{item.title}</h3>
                      {item.description && <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>}
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
